/**
 * App-side first-touch acquisition capture (insights Phase 2B). Once per browser
 * session, POST the landing URL's UTM params + the (cross-origin) referrer to
 * `POST /api/first-touch` so the server records ONE `ActorFirstTouch` row per
 * principal (create-only: first touch wins, repeat calls are harmless 202s).
 *
 * The call is UNCONDITIONAL: a direct visit carries no UTMs and no cross-origin
 * referrer, and posts an EMPTY body — which the server records as a `direct`
 * first touch. Skipping it would make direct traffic indistinguishable from
 * visitors the app simply never instrumented.
 *
 * Fired at boot from `<AuthProvider>` once the principal is established, so the
 * request rides the standard `apiCall` (CSRF + anon-carry headers). Boot-time,
 * so `keepalive` is unnecessary; fire-and-forget with errors swallowed — an
 * attribution beacon must never surface in the UX.
 *
 * A `sessionStorage` guard suppresses the repeat POST within a tab session. It
 * is noise reduction only — the server endpoint is create-only, so a missed
 * guard (private mode / blocked storage) just costs one no-op round-trip. The
 * guard is committed only AFTER the POST resolves and cleared on failure, so a
 * transient first-paint failure (the CSRF race) doesn't permanently drop the
 * row — the next call (self-healing refreshCsrf → applyResponse) retries.
 */
import { apiCall } from "./api";

const SESSION_GUARD_KEY = "fg.firstTouchSent";

type FirstTouchBody = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrer?: string;
};

/**
 * Read the landing URL's UTM params + a cross-origin referrer into the POST
 * body. ALWAYS returns a body — an EMPTY one (`{}`) is the meaningful signal
 * for a DIRECT visit (no UTMs, and either no referrer or a same-origin one, so
 * no acquisition source to name). The server's schema marks every field
 * optional and `normalizeFirstTouch` categorizes an all-empty payload as
 * `direct`, so sending it is what distinguishes "arrived directly" from "the
 * app never instrumented this visitor".
 *
 * Only DEFINED fields are included, so a direct visit serializes to exactly
 * `{}`. The server sanitizes every field (URL-shaped UTMs rejected, referrer
 * reduced to host-only), so this stays deliberately permissive.
 */
export function collectFirstTouch(
  search: string,
  referrer: string,
  origin: string
): FirstTouchBody {
  const params = new URLSearchParams(search);
  const utmSource = params.get("utm_source") ?? undefined;
  const utmMedium = params.get("utm_medium") ?? undefined;
  const utmCampaign = params.get("utm_campaign") ?? undefined;

  // A referrer only counts when it's non-empty AND cross-origin — a same-origin
  // referrer is an internal navigation, not an acquisition source.
  let crossOriginReferrer: string | undefined;
  if (referrer && referrer.trim()) {
    try {
      if (new URL(referrer).origin !== origin) crossOriginReferrer = referrer;
    } catch {
      // Unparseable referrer — let the server decide (it host-reduces + rejects).
      crossOriginReferrer = referrer;
    }
  }

  const body: FirstTouchBody = {};
  if (utmSource) body.utmSource = utmSource;
  if (utmMedium) body.utmMedium = utmMedium;
  if (utmCampaign) body.utmCampaign = utmCampaign;
  if (crossOriginReferrer) body.referrer = crossOriginReferrer;
  return body;
}

/**
 * Fire the first-touch capture once per browser session — ALWAYS, including
 * direct visits (empty body). Suppressed only when already sent this session or
 * without a CSRF token (the POST is a mutation). Best-effort: all failures are
 * swallowed.
 */
export function reportFirstTouch(csrfToken: string | null): void {
  if (typeof window === "undefined" || !csrfToken) return;
  try {
    if (sessionStorage.getItem(SESSION_GUARD_KEY)) return;
  } catch {
    /* ignore — private mode / blocked storage; the server is create-only */
  }
  // Sent even when empty: `{}` records a DIRECT first touch, which is what makes
  // direct traffic distinguishable from un-instrumented traffic.
  const body = collectFirstTouch(
    window.location.search,
    document.referrer,
    window.location.origin
  );
  // Optimistically mark sent to suppress a concurrent double-fire, but clear it
  // on failure so a later call can retry (the endpoint is create-only, so at
  // worst a success we didn't observe costs one no-op repeat).
  try {
    sessionStorage.setItem(SESSION_GUARD_KEY, "1");
  } catch {
    /* ignore — the create-only endpoint tolerates the repeat */
  }
  void apiCall("/api/first-touch", {
    method: "POST",
    body,
    csrfToken
  }).catch(() => {
    try {
      sessionStorage.removeItem(SESSION_GUARD_KEY);
    } catch {
      /* ignore — blocked storage; the create-only endpoint tolerates a repeat */
    }
  });
}
